import React, { useEffect, useState } from "react";
import { X, Loader2, Users, Check } from "lucide-react";
import toast from "react-hot-toast";

import { getProposalTeammates, setProposalCollaborators } from "../api/prospectingReportsApi";

const initials = (name, email) => ((name || email || "?").trim()[0] || "?").toUpperCase();

const CheckBox = ({ on }) => (
  <span className={`w-5 h-5 rounded-md border flex items-center justify-center ${on ? "bg-[#0f8d9b] border-[#0f8d9b]" : "border-gray-300"}`}>
    {on && <Check className="w-3.5 h-3.5 text-white" />}
  </span>
);

/**
 * Proply "Manage teammates": collaborate with specific users or share the whole
 * proposal with the entire team (all existing and future CRM staff).
 */
const ManageTeammatesModal = ({ proposal, onClose, onSaved }) => {
  const [mates, setMates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entireTeam, setEntireTeam] = useState(!!proposal.shared_with_team);
  const [selected, setSelected] = useState(new Set((proposal.collaborators || []).map((c) => c.id)));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getProposalTeammates()
      .then(({ data }) => setMates(data?.results || []))
      .catch(() => toast.error("Could not load teammates."))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await setProposalCollaborators(proposal.id, {
        user_ids: Array.from(selected), entire_team: entireTeam,
      });
      onSaved && onSaved({ collaborators: data.collaborators, shared_with_team: data.shared_with_team });
      toast.success("Sharing updated.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">
        <div className="px-6 pt-5 pb-3">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Manage teammates</h2>
              <p className="text-sm text-gray-500 mt-0.5">Collaborate with specific users or add your entire team to this proposal.</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6">
          <button type="button" onClick={() => setEntireTeam((v) => !v)}
            className="w-full flex items-center gap-3 py-3 border-b border-gray-100">
            <span className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-600"><Users className="w-4 h-4" /></span>
            <span className="flex-1 text-left">
              <span className="block font-semibold text-gray-900">Entire team</span>
              <span className="block text-xs text-gray-500">All existing and future users</span>
            </span>
            <CheckBox on={entireTeam} />
          </button>

          {loading ? (
            <div className="flex justify-center py-10 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <div className={entireTeam ? "opacity-40 pointer-events-none" : ""}>
              {mates.map((m) => (
                <button key={m.id} type="button" onClick={() => toggle(m.id)}
                  className="w-full flex items-center gap-3 py-3 border-b border-gray-50">
                  <span className="w-9 h-9 rounded-full bg-gray-800 text-white flex items-center justify-center text-sm font-semibold">{initials(m.name, m.email)}</span>
                  <span className="flex-1 text-left min-w-0">
                    <span className="block font-semibold text-gray-900 truncate">{m.name || m.email}</span>
                    {m.email && <span className="block text-xs text-gray-500 truncate">{m.email}</span>}
                  </span>
                  <CheckBox on={selected.has(m.id)} />
                </button>
              ))}
              {!mates.length && <p className="text-sm text-gray-400 py-6 text-center">No teammates found.</p>}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600">Cancel</button>
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0f8d9b] text-white text-sm font-semibold disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageTeammatesModal;
